import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";

interface SaveBarProps {
  dirtyCount: number;
  dirtySummary: string;
  errors: string[];
  saving: boolean;
  canEdit: boolean;
  /** An own row exists at this scope and the scope has a parent — "Revert everything" is offered. */
  canRevertRow: boolean;
  onCancel: () => void;
  onSave: () => Promise<void>;
  onRevertRow: () => Promise<void>;
}

/** Sticky footer of the type panel: what is staged, why it cannot save, Cancel / Save. */
export function SaveBar({
  dirtyCount,
  dirtySummary,
  errors,
  saving,
  canEdit,
  canRevertRow,
  onCancel,
  onSave,
  onRevertRow
}: SaveBarProps): React.ReactElement | null {
  if (!canEdit) return null;
  if (dirtyCount === 0 && !canRevertRow) return null;

  const handleRevertRow = async (): Promise<void> => {
    if (!window.confirm("Revert everything to inherited? This deletes every setting saved at this scope for this sequence.")) return;
    await onRevertRow();
  };

  return (
    <div className="sticky bottom-0 z-100 border-t border-grey-200 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
      <div className="mx-auto flex max-w-[960px] flex-col gap-2 px-6 py-3">
        {errors.length > 0 && (
          <ul className="body-sm list-disc rounded-sm border border-red-300 bg-red-50 px-3 py-2 pl-7 text-red-700">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between gap-4">
          <div className="body-sm text-grey-700">
            {dirtyCount > 0 ? (
              <>
                <span className="body-sm-alt text-grey-900">
                  {dirtyCount} {dirtyCount === 1 ? "change" : "changes"}
                </span>
                {dirtySummary && <span className="text-grey-600"> · {dirtySummary}</span>}
              </>
            ) : (
              <span className="text-grey-600">No unsaved changes</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canRevertRow && dirtyCount === 0 && (
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClickAsync={handleRevertRow}>
                Revert everything to inherited
              </Button>
            )}
            {dirtyCount > 0 && (
              <>
                <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={onCancel}>
                  Cancel
                </Button>
                <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={saving || errors.length > 0} onClickAsync={onSave}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
